import { useEffect, useRef, useState } from 'react';
import { FileText, PenLine, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/button';

interface EsignDoc {
  id: string;
  title: string;
  filename: string;
  download_url: string;
}

interface DocEsignStepProps {
  esignDocs: EsignDoc[];
  signedDocIds: string[];
  onDocSigned: (docId: string) => void;
  onContinue: () => void;
  canContinue: boolean;
}

export function DocEsignStep({
  esignDocs,
  signedDocIds,
  onDocSigned,
  onContinue,
  canContinue,
}: DocEsignStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [openedDocs, setOpenedDocs] = useState<Set<string>>(new Set());

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const handleOpenDoc = (doc: EsignDoc) => {
    // Open the document in a new tab for signing
    window.open(doc.download_url, '_blank', 'noopener,noreferrer');
    setOpenedDocs(prev => new Set(Array.from(prev).concat(doc.id)));
  };

  const allSigned = esignDocs.every(d => signedDocIds.includes(d.id));

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl mb-2 focus-visible:outline-none"
        >
          Signature Required
        </h1>
        <p className="text-lg text-muted-foreground">
          The following documents require your electronic signature before investing.
        </p>
      </div>

      <div className="space-y-3">
        {esignDocs.map((doc) => {
          const isSigned = signedDocIds.includes(doc.id);
          const wasOpened = openedDocs.has(doc.id);

          return (
            <div
              key={doc.id}
              className={`rounded-xl border-2 p-4 flex items-center justify-between gap-4 transition-colors ${
                isSigned ? 'border-green-300 bg-green-50' : 'border-border bg-card'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {isSigned ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                ) : (
                  <FileText className="w-5 h-5 text-primary shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{doc.title || doc.filename}</p>
                  {isSigned && (
                    <p className="text-xs text-green-600">Signed</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {!isSigned && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleOpenDoc(doc)}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Review
                    </button>
                    {wasOpened && (
                      <button
                        type="button"
                        onClick={() => onDocSigned(doc.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                      >
                        <PenLine className="w-3.5 h-3.5" />
                        Mark Signed
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!allSigned && (
        <p className="text-sm text-muted-foreground text-center">
          Please review and sign all required documents to continue.
        </p>
      )}

      <Button
        className="w-full h-14 text-lg disabled:opacity-40"
        onClick={onContinue}
        disabled={!canContinue}
      >
        Continue to Sign Agreement
      </Button>
    </div>
  );
}
